import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import {
  WorkoutResponseDto,
  GetWorkoutsParams,
  PaginatedWorkoutsResponseDto,
  CreateWorkoutDto,
  UpdateWorkoutDto,
  MergeWorkoutDto,
  Locale,
  WorkoutSuggestion,
  WorkoutCategory,
  MeasurementUnit
} from './workout.service';
import { ImportResult, ImportSession } from '../types/import.types';

export interface WorkoutOptionsResponseDto {
  categories: { value: string; label: string }[];
  measurementUnits: { value: string; label: string }[];
  experts: { id: number; name: string }[];
  sourceTypes: { value: string; label: string }[];
}

export interface MetActivityDto {
  id: number;
  name: string;
  nameEn: string;
  metValue: number;
  compendiumType: string;
  categoryName: string;
}

// Interface for the edit response which includes nameI18n
export interface WorkoutEditResponseDto extends WorkoutResponseDto {
  nameI18n: { [key in Locale]?: string };
  detailsI18n?: { [key in Locale]?: string }; // Added detailsI18n
  metActivity?: MetActivityDto | null; // Added metActivity
}

// Interface for the detailed response
export interface WorkoutDetailDto {
  id: number;
  nameI18n: { [key in Locale]?: string };
  detailsI18n?: { [key in Locale]?: string }; // Added detailsI18n
  category: WorkoutCategory;
  measurementUnit: MeasurementUnit;
  
  metValue: number | null;
  metActivityId: number | null;
  metActivity?: MetActivityDto | null; // Added metActivity
  defaultMeasurementValue: number | null;

  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  imageId: number | null;
  imageUrl: string | null;
  externalLink: string | null;

  createdByUserId: number | null;
  creator: {
    id: number;
    fullName: string;
    email: string;
  } | null;

  usageCount: number;
  createdAt: string;
  updatedAt: string;

  legacyId?: string | null; // Added legacyId
  legacyEntity?: 'exercise' | null; // Added legacyEntity
  legacyImg?: string | null; // Added legacyImg
}

export type ImportWorkoutsResponseDto = ImportResult;
export type ImportSessionDto = ImportSession;

class AdminWorkoutService {
  private readonly BASE_URL = '/admin/lib/workouts';
  private readonly IMPORT_BASE_URL = '/admin/lib/import';

  async getWorkouts(params: GetWorkoutsParams): Promise<PaginatedWorkoutsResponseDto> {
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });

    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return {
        data: body.data,
        meta: body.meta,
      };
    }
    
    // Fallback for different response structure
    if (body && Array.isArray(body.data)) {
       return {
        data: body.data,
        meta: body.meta || { total: body.data.length, page: 1, limit: params.limit || 10 },
      };
    }
    
    console.error('Unexpected API response structure for admin getWorkouts:', body);
    return { 
      data: [], 
      meta: { total: 0, page: 1, limit: params.limit || 10 } 
    };
  }
  
  async getWorkoutSuggestions(query: string, limit: number = 10): Promise<WorkoutSuggestion[]> {
    const params: GetWorkoutsParams = {
      search: query,
      limit: limit,
    };
    const response = await this.getWorkouts(params);

    if (!response || !Array.isArray(response.data)) {
      return [];
    }
    
    return response.data.map(workout => ({
      id: workout.id,
      name: workout.name,
      category: workout.category,
      measurementUnit: workout.measurementUnit,
      rawMeasurementUnit: workout.rawMeasurementUnit,
      metValue: workout.metValue,
      metActivityId: workout.metActivityId,
      defaultMeasurementValue: workout.defaultMeasurementValue,
      isStructured: workout.isStructured,
      isSystem: workout.isSystem,
    }));
  }

  async createWorkout(payload: CreateWorkoutDto): Promise<WorkoutResponseDto> {
    const response = await api.post<ApiResponse<WorkoutResponseDto>>(`${this.BASE_URL}`, payload);
    return unwrapApiResponse<WorkoutResponseDto>(response);
  }

  async getWorkoutForEdit(id: number): Promise<WorkoutEditResponseDto> {
    // Use the /edit endpoint to get full data including translations
    const response = await api.get<ApiResponse<WorkoutEditResponseDto>>(`${this.BASE_URL}/${id}/edit`);
    return unwrapApiResponse<WorkoutEditResponseDto>(response);
  }

  async getWorkoutDetail(id: number): Promise<WorkoutDetailDto> {
    const response = await api.get<ApiResponse<WorkoutDetailDto>>(`${this.BASE_URL}/${id}/detail`);
    return unwrapApiResponse<WorkoutDetailDto>(response);
  }

  async updateWorkout(id: number, payload: UpdateWorkoutDto): Promise<WorkoutResponseDto> {
    const response = await api.patch<ApiResponse<WorkoutResponseDto>>(`${this.BASE_URL}/${id}`, payload);
    return unwrapApiResponse<WorkoutResponseDto>(response);
  }

  async deleteWorkout(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.BASE_URL}/${id}`);
  }

  async mergeWorkouts(payload: MergeWorkoutDto): Promise<{ removedWorkout: WorkoutResponseDto; targetWorkout: WorkoutResponseDto; updatedCalendarEvents: number }> {
    const response = await api.post<ApiResponse<{ removedWorkout: WorkoutResponseDto; targetWorkout: WorkoutResponseDto; updatedCalendarEvents: number }>>(`${this.BASE_URL}/merge`, payload);
    return unwrapApiResponse<{ removedWorkout: WorkoutResponseDto; targetWorkout: WorkoutResponseDto; updatedCalendarEvents: number }>(response);
  }

  // Options
  async getWorkoutOptions(): Promise<WorkoutOptionsResponseDto> {
    const response = await api.get<ApiResponse<WorkoutOptionsResponseDto>>(`${this.BASE_URL}/options`);
    return unwrapApiResponse<WorkoutOptionsResponseDto>(response);
  }

  // Import functionality
  async downloadImportTemplate(): Promise<Blob> {
    const response = await api.get(`${this.IMPORT_BASE_URL}/workouts/template`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importWorkouts(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<ImportResult>>(`${this.IMPORT_BASE_URL}/workouts`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return unwrapApiResponse<ImportResult>(response);
  }

  async getImportHistory(): Promise<ImportSession[]> {
    const response = await api.get<ApiResponse<ImportSession[]>>(`${this.IMPORT_BASE_URL}/history`);
    return unwrapApiResponse<ImportSession[]>(response);
  }

  async revertImport(sessionId: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.IMPORT_BASE_URL}/${sessionId}`);
  }
}

export const adminWorkoutService = new AdminWorkoutService();
